import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import api, { apiErr, downloadBlob, blobErrDetail } from "@/lib/api";
import { Download, FileText, FileSpreadsheet, Sheet, MessageCircle, Loader2 } from "lucide-react";

const PERIODS = [["all", "Tout"], ["today", "Aujourd'hui"], ["7", "7 jours"], ["30", "30 jours"], ["365", "Cette année"]];

function rangeFor(p) {
  if (p === "all") return {};
  const to = new Date();
  const from = new Date();
  if (p !== "today") from.setDate(to.getDate() - Number(p) + 1);
  const iso = (d) => d.toISOString().slice(0, 10);
  return { from_date: iso(from), to_date: iso(to) };
}

export const ExportMenu = ({ dataset, params = {}, testId }) => {
  const [open, setOpen] = useState(false);
  const [period, setPeriod] = useState("30");
  const [busy, setBusy] = useState(false);
  const btnRef = useRef(null);
  const [pos, setPos] = useState(null);

  const place = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const width = Math.min(224, window.innerWidth - 24);
    let left = r.right - width;
    left = Math.max(12, Math.min(left, window.innerWidth - width - 12));
    const top = Math.min(r.bottom + 6, window.innerHeight - 12);
    setPos({ top, left, width });
  };
  const toggle = () => { if (!open) place(); setOpen((v) => !v); };

  useEffect(() => {
    if (!open) return;
    const h = () => place();
    window.addEventListener("resize", h);
    window.addEventListener("scroll", h, true);
    return () => { window.removeEventListener("resize", h); window.removeEventListener("scroll", h, true); };
  }, [open]);

  const qs = () => {
    const all = { ...params, ...rangeFor(period) };
    return Object.entries(all)
      .filter(([, v]) => v != null && v !== "")
      .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
      .join("&");
  };
  const MIME = {
    csv: "text/csv;charset=utf-8;",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    pdf: "application/pdf",
  };
  const download = async (fmt) => {
    setBusy(true);
    try {
      const res = await api.get(`/exports/${dataset}?${qs()}&format=${fmt}`, { responseType: "blob" });
      const cd = res.headers?.["content-disposition"] || "";
      const m = /filename="?([^";]+)"?/.exec(cd);
      const name = m ? m[1] : `${dataset}.${fmt}`;
      downloadBlob(new Blob([res.data], { type: MIME[fmt] || "application/octet-stream" }), name);
      setOpen(false);
    } catch (e) {
      toast.error(apiErr(await blobErrDetail(e), e));
    } finally { setBusy(false); }
  };
  const share = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/exports/${dataset}/share?${qs()}`);
      window.open(`https://wa.me/?text=${encodeURIComponent(data.url)}`, "_blank", "noopener,noreferrer");
      setOpen(false);
    } catch (e) { toast.error("Partage impossible"); }
    finally { setBusy(false); }
  };

  const item = "w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-slate-50 flex items-center gap-2";
  return (
    <div className="relative">
      <button ref={btnRef} onClick={toggle} className="btn-outline" data-testid={testId || `export-${dataset}-btn`}><Download className="w-4 h-4" /> Exporter</button>
      {open && pos && createPortal(
        <>
          <div className="fixed inset-0 z-[998]" onClick={() => setOpen(false)} data-testid={`export-${dataset}-overlay`} />
          <div className="fixed z-[999] card p-2" style={{ top: pos.top, left: pos.left, width: pos.width, maxHeight: "calc(100vh - 24px)", overflowY: "auto" }} data-testid={`export-${dataset}-menu`}>
            <div className="px-1 pb-2">
              <label className="lbl">Période</label>
              <select className="inp !py-1.5" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid={`export-${dataset}-period`}>
                {PERIODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <button onClick={() => download("pdf")} disabled={busy} className={`${item} disabled:opacity-50`} data-testid={`export-${dataset}-pdf`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4 text-[#B91C1C]" />} PDF</button>
            <button onClick={() => download("xlsx")} disabled={busy} className={`${item} disabled:opacity-50`} data-testid={`export-${dataset}-xlsx`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 text-[#15803D]" />} Excel (.xlsx)</button>
            <button onClick={() => download("csv")} disabled={busy} className={`${item} disabled:opacity-50`} data-testid={`export-${dataset}-csv`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sheet className="w-4 h-4 text-[#0E7490]" />} CSV</button>
            <button onClick={share} disabled={busy} className={item} data-testid={`export-${dataset}-wa`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4 text-[#15803D]" />} Partager (WhatsApp)</button>
          </div>
        </>,
        document.body
      )}
    </div>
  );
};
